import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Questionnaire,
  QuestionnaireClient,
  QuestionnaireResponse,
} from '../../../infrastructure/QuestionnaireClient';

export const useSurveyClient = () => useMemo(() => new QuestionnaireClient(), []);

export function useSurveyList() {
  const client = useSurveyClient();
  const [surveys, setSurveys] = useState<Questionnaire[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSurveys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await client.listQuestionnaires();
      const enriched = await Promise.all(
        list.map(async (survey) => {
          try {
            const answers = await client.listResponses(survey.id);
            return { ...survey, responseCount: answers.length };
          } catch {
            return survey;
          }
        }),
      );
      setSurveys(enriched);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load surveys');
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void loadSurveys();
  }, [loadSurveys]);

  return { client, surveys, isLoading, error, reload: loadSurveys };
}

export function useSurveyDetail(surveyId: string | undefined) {
  const { client, surveys, isLoading, error, reload } = useSurveyList();
  const isNew = surveyId === 'new';
  const numericSurveyId = surveyId && !isNew ? Number(surveyId) : null;
  const survey = numericSurveyId ? surveys.find((item) => item.id === numericSurveyId) ?? null : null;

  return {
    client,
    surveys,
    survey,
    surveyId: numericSurveyId,
    isNew,
    isLoading,
    error,
    reload,
  };
}

export function useSurveyAnswers(surveyId: number | null) {
  const client = useSurveyClient();
  const [answers, setAnswers] = useState<QuestionnaireResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnswers = useCallback(async () => {
    if (!surveyId) {
      setAnswers([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      setAnswers(await client.listResponses(surveyId));
    } catch (loadError) {
      setAnswers([]);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load answers');
    } finally {
      setIsLoading(false);
    }
  }, [client, surveyId]);

  useEffect(() => {
    void loadAnswers();
  }, [loadAnswers]);

  return { answers, isLoading, error, reload: loadAnswers };
}
